import { z } from "zod";
import {
  GROUP_CODE_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  USERNAME_REQUIREMENTS_MESSAGE,
  PASSWORD_REQUIREMENTS_MESSAGE,
} from "@/constants";
import { isValidPhoneNumber } from "react-phone-number-input";

/* eslint-disable no-control-regex */
const EMAIL_VALIDATION_REGEX =
  /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|(?:\[(?:(?:IPv6:[a-fA-F0-9:.]+)|(?:[0-9]{1,3}\.){3}[0-9]{1,3})]))$/;
/* eslint-enable no-control-regex */

// Shared, reusable, modular field validation. For now starting with email. We're gradually migrating schemas below.
// Please use `emailSchema` for any new email fields, and feel free to migrate an existing schema to it when you're touching it anyway.
export const emailSchema = z
  .string()
  .min(1, "Email is required.")
  .regex(EMAIL_VALIDATION_REGEX, "Enter a valid email address.");

// --------

export const balanceBillSchema = z.object({
  claimNumber: z
    .string()
    .nonempty("Claim Number cannot be empty.")
    .max(30, "Claim Number must be 3-30 characters.")
    .refine((val) => /^[A-Za-z0-9-]{3,30}$/.test(val), {
      message: "Claim Number must be 3-30 characters, alphanumeric or hyphen.",
    }),
  balanceBillAmt: z
    .string()
    .trim()
    .nonempty("Balance Bill Amount cannot be empty.")
    .regex(/^\d+(\.\d{1,2})?$/, "Enter a valid amount.")
    .refine((val) => Number(val) > 0, "Enter a valid amount."),
  billedAmt: z
    .string()
    .trim()
    .nonempty("Amount Billed cannot be empty.")
    .regex(/^\d+(\.\d{1,2})?$/, "Enter a valid amount.")
    .refine((val) => Number(val) > 0, "Enter a valid amount."),
  patientName: z
    .string()
    .trim()
    .nonempty("Patient Name cannot be empty.")
    .max(50, "Patient Name cannot exceed 50 characters.")
    .regex(/^[A-Za-z\s.'-]{2,100}$/, "Enter a valid name."),
  dateOfService: z
    .date({
      required_error: "Date of Service cannot be empty.",
      invalid_type_error: "Invalid date.",
    })
    .refine(
      (date) => date < new Date(),
      "Date of Service must be in the past.",
    ),

  providerName: z
    .string()
    .trim()
    .nonempty("Provider cannot be empty.")
    .min(2, "Provider name must be 2-50 characters.")
    .max(50, "Provider name must be 2-50 characters."),

  additionalInformation: z
    .string()
    .max(200, "Information cannot exceed 200 characters.")
    .optional(),
  // attachments: ,
});

export const locatePageSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name is required.")
    .max(50, "First name must be 50 characters or fewer."),

  lastName: z
    .string()
    .min(2, "Last name is required.")
    .max(50, "Last name must be 50 characters or fewer."),

  zipCode: z
    .string()
    .min(5, "Zip code must be at least 5 digits.")
    .max(10, "Zip code must be 10 digits or fewer.")
    .regex(/^\d{5}(-\d{4})?$/, "Enter a valid zip code."),
  dob: z
    .date({
      required_error: "Date of birth is required.",
      invalid_type_error: "Invalid date.",
    })
    .refine((date) => date < new Date(), "Date of birth must be in the past."),
});

export const groupPageSchema = z.object({
  groupCode: z.string().nonempty("Group Code is required."),
  // .min(
  //   GROUP_CODE_MIN_LENGTH,
  //   `Group code must be at least ${GROUP_CODE_MIN_LENGTH} characters long`,
  // )
});

export const loginPageSchema = z.object({
  userName: z.string().nonempty("Username is required."),
  // .min(USERNAME_MIN_LENGTH, USERNAME_REQUIREMENTS_MESSAGE)
  password: z.string().nonempty("Password is required."),
  isRemembered: z.boolean(),
});

export const resetPageSchema = z.object({
  userName: z.string().nonempty("Username is required."),
  email: z
    .string()
    .nonempty("Email is required.")

    .regex(EMAIL_VALIDATION_REGEX, "Enter a valid email address."),
});

export const changePasswordSchema = z.object({
  password: z.string().nonempty("Current password is required."),
  newPassword: z
    .string()
    .min(8, PASSWORD_REQUIREMENTS_MESSAGE)
    .max(64, "Password must be at most 64 characters long.")
    .regex(/[a-z]/, "Password must include a lowercase letter.")
    .regex(/[A-Z]/, "Password must include a capital letter.")
    .regex(/[0-9]/, "Password must include a number.")
    .regex(/[^a-zA-Z0-9]/, "Password must include a symbol.")
    .regex(/^\S*$/, "Password must not include spaces."),
  confirmPassword: z.string().nonempty("Please confirm your password."),
});

export const registerPageSchema = z.object({
  groupCode: z
    .string()
    .min(1, "Group code is required.")
    .max(
      GROUP_CODE_MAX_LENGTH,
      `Group code must be at most ${GROUP_CODE_MAX_LENGTH} characters long.`,
    )
    .optional(),
  userName: z
    .string()
    .min(USERNAME_MIN_LENGTH, USERNAME_REQUIREMENTS_MESSAGE)
    .max(USERNAME_MAX_LENGTH, USERNAME_REQUIREMENTS_MESSAGE)
    .refine((val) => !/\s/.test(val), {
      message: "No spaces allowed.",
    })
    .refine((val) => /^[a-zA-Z0-9!@#$%^&*)(+=._-]+$/.test(val), {
      message: "Invalid characters used.",
    }),
  password: z
    .string()
    .min(8, PASSWORD_REQUIREMENTS_MESSAGE)
    .max(64, "Password must be at most 64 characters long.")
    .regex(/[a-z]/, "Password must include a lowercase letter.")
    .regex(/[A-Z]/, "Password must include a capital letter.")
    .regex(/[0-9]/, "Password must include a number.")
    .regex(/[^a-zA-Z0-9]/, "Password must include a symbol.")
    .regex(/^\S*$/, "Password must not include spaces."),
  confirmPassword: z.string().nonempty("Please confirm your password."),
  firstName: z
    .string()
    .min(2, "First name is required.")
    .max(50, "First name must be 50 characters or fewer."),
  lastName: z
    .string()
    .min(2, "Last name is required.")
    .max(50, "Last name must be 50 characters or fewer."),
  zipCode: z
    .string()
    .min(5, "Zip code must be at least 5 digits.")
    .max(10, "Zip code must be 10 digits or fewer.")
    .regex(/^\d{5}(-\d{4})?$/, "Enter a valid zip code.")
    .optional(),
  dob: z.date({
    required_error: "Date of birth is required.",
  }),
  email: z
    .string()
    .min(1, "Email is required.")

    .regex(EMAIL_VALIDATION_REGEX, "Enter a valid email address."),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number." }),
  ssn: z
    .string()
    .length(4, { message: "SSN last 4 digits must be exactly 4 characters." })
    .regex(/^\d{4}$/, {
      message: "SSN last 4 digits must contain only digits.",
    })
    .optional(),
  memberId: z
    .string()
    .min(1, "Member Id is required.")
    .max(
      USERNAME_MAX_LENGTH, // for now making memberId max length as 25 (same as username)
      `Member Id must be at most ${USERNAME_MAX_LENGTH} characters long.`,
    )
    .optional(),
});

export const resetPasswordSchema = z.object({
  otp: z
    .string()
    .length(6, "OTP must be 6 digits.")
    .regex(/^\d+$/, "OTP must contain only numbers."),
  password: z
    .string()
    .nonempty("Password is required.")
    .min(8, PASSWORD_REQUIREMENTS_MESSAGE)
    .max(64, "Password must be at most 64 characters long.")
    .regex(/[a-z]/, "Password must include a lowercase letter.")
    .regex(/[A-Z]/, "Password must include a capital letter.")
    .regex(/[0-9]/, "Password must include a number.")
    .regex(/[^a-zA-Z0-9]/, "Password must include a symbol.")
    .regex(/^\S*$/, "Password must not include spaces."),
  confirmPassword: z.string().nonempty("Please confirm your password."),
});

export const messageSupportSchema = z.object({
  name: z
    .string({ required_error: "Name is required." })
    .min(1, "Name is required.")
    .max(100, "Exceeded maximum characters."),
  email: z
    .string()
    .min(1, "Email is required.")

    .regex(EMAIL_VALIDATION_REGEX, "Enter a valid email address."),
  message: z.string().min(1, "Message is required."),
});

export const inviteUserSchema = z.object({
  email: emailSchema,
});

export const balanceBillConfirmationSchema = z.object({
  additionalInfo: z
    .string()
    .max(200, "Maximum length is 200 characters.")
    .optional(),
  email: z
    .string()
    .min(1, "Email is required.")

    .regex(EMAIL_VALIDATION_REGEX, "Enter a valid email address."),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number." }),
});
